import React, { useState, useRef } from 'react';
import { Card, Button, TextArea, Modal } from 'antd-mobile';
import { EditSOutline } from 'antd-mobile-icons';
import { useStore } from '../store/useStore';

export const MotivationCard: React.FC = () => {
    const { motivation, setMotivation } = useStore();
    const [isEditing, setIsEditing] = useState(false);
    const [tempText, setTempText] = useState(motivation || '');
    const [uploadedImage, setUploadedImage] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Load image from localStorage if exists (simple Hack for MVP)
    // Key: 'puff-tracker-motivation-image'
    React.useEffect(() => {
        const savedImage = localStorage.getItem('puff-tracker-motivation-image');
        if (savedImage) setUploadedImage(savedImage);
        if (motivation) setTempText(motivation);
    }, [motivation]);

    const handleSave = () => {
        setMotivation(tempText);
        setIsEditing(false);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64 = reader.result as string;
                setUploadedImage(base64);
                localStorage.setItem('puff-tracker-motivation-image', base64);
            };
            reader.readAsDataURL(file);
        }
    };

    if (!motivation && !isEditing && !uploadedImage) {
        return (
            <Card style={{
                borderRadius: 24,
                marginBottom: 24,
                border: '2px dashed #cbd5e1',
                background: '#f8fafc',
                cursor: 'pointer'
            }} onClick={() => setIsEditing(true)}>
                <div style={{ textAlign: 'center', padding: '16px 0' }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>❤️</div>
                    <div style={{ fontWeight: 600, color: '#64748b' }}>เพิ่มแรงบันดาลใจของคุณ</div>
                    <div style={{ fontSize: 12, color: '#94a3b8' }}>ทำไมคถึงอยากเลิกสูบ?</div>
                </div>
            </Card>
        );
    }

    return (
        <>
            <Card style={{
                borderRadius: 24,
                marginBottom: 24,
                background: uploadedImage ? `url(${uploadedImage}) center/cover no-repeat` : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                color: 'white',
                position: 'relative',
                overflow: 'hidden',
                boxShadow: '0 8px 24px rgba(239, 68, 68, 0.25)',
                minHeight: 160
            }}>
                {/* Overlay for text readability if image exists */}
                {uploadedImage && <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} />}

                <div style={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 120 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{
                            background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)',
                            padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                            display: 'inline-flex', alignItems: 'center', gap: 6
                        }}>
                            ❤️ My Why
                        </div>
                        <Button
                            size='mini'
                            fill='none'
                            style={{ color: 'white', padding: 4 }}
                            onClick={() => {
                                setTempText(motivation || '');
                                setIsEditing(true);
                            }}
                        >
                            <EditSOutline fontSize={20} />
                        </Button>
                    </div>

                    <div style={{ marginTop: 16 }}>
                        <div style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.4, textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                            {motivation || "เพิ่มข้อความเตือนใจของคุณ"}
                        </div>
                    </div>
                </div>
            </Card>

            <Modal
                visible={isEditing}
                title="แรงบันดาลใจของฉัน"
                content={
                    <div>
                        <div style={{ marginBottom: 16 }}>
                            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8, color: '#64748b' }}>รูปภาพเตือนใจ</div>
                            <input
                                type="file"
                                accept="image/*"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                style={{ display: 'none' }}
                            />
                            <div
                                onClick={() => fileInputRef.current?.click()}
                                style={{
                                    height: 100, borderRadius: 12, border: '1px dashed #cbd5e1',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    background: uploadedImage ? `url(${uploadedImage}) center/cover` : '#f1f5f9',
                                    cursor: 'pointer', overflow: 'hidden'
                                }}
                            >
                                {!uploadedImage && <span style={{ color: '#94a3b8' }}>+ อัปโหลดรูป</span>}
                            </div>
                            {uploadedImage && (
                                <div
                                    style={{ color: '#ef4444', fontSize: 12, marginTop: 4, textAlign: 'right', cursor: 'pointer' }}
                                    onClick={() => {
                                        setUploadedImage(null);
                                        localStorage.removeItem('puff-tracker-motivation-image');
                                    }}
                                >
                                    ลบรูปภาพ
                                </div>
                            )}
                        </div>

                        <div>
                            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8, color: '#64748b' }}>ข้อความให้กำลังใจ</div>
                            <TextArea
                                value={tempText}
                                onChange={setTempText}
                                placeholder="เช่น เพื่อครอบครัว, เพื่อสุขภาพที่ดีขึ้น..."
                                rows={3}
                                style={{ background: '#f8fafc', padding: 8, borderRadius: 8 }}
                            />
                        </div>
                    </div>
                }
                closeOnAction
                onClose={() => setIsEditing(false)}
                actions={[
                    {
                        key: 'save',
                        text: 'บันทึก',
                        primary: true,
                        onClick: handleSave,
                    },
                ]}
            />
        </>
    );
};
